import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Users, Copy, Share2, Gift, CheckCircle, Loader, PlusCircle } from 'lucide-react';
import { toast } from 'react-toastify';
import DashboardLayout from '../components/DashboardLayout';
import { useUser } from '../context/UserContext';

const API_BASE = 'https://tradebot-k4nt.onrender.com/api';
const SITE_URL = 'https://modanmic.com.ng';

const Referral = () => {
  const navigate = useNavigate();
  const { user: contextUser } = useUser();
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!contextUser?.telegramName) { navigate('/login'); return; }
    fetchUserData(contextUser.telegramName);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contextUser?.telegramName]);

  // Mirrors retriveAccountLocalUpdate for refer.html
  const fetchUserData = async (username) => {
    try {
      const response = await fetch(`${API_BASE}/users/${username}`);
      const data = await response.json();
      // usernameInfo can be an array or a direct object
      let info = data?.usernameInfo;
      if (Array.isArray(info)) info = info[0];
      if (response.status === 200 && info) {
        setUserData(info);
      } else {
        toast.error('Could not load referral data');
      }
    } catch {
      toast.error('Network error loading referral data');
    } finally {
      setLoading(false);
    }
  };

  // Exactly mirrors handelClickAddRefBalace in index.js
  const handleAddRefBalance = async (event) => {
    event.preventDefault();
    const refBalance = Number(userData?.refralBalance ?? 0);
    if (refBalance <= 0) {
      toast.error('No referral balance available to add');
      return;
    }
    setAdding(true);
    try {
      const response = await fetch(`${API_BASE}/payment/addToBalanceRef/${userData.telegramName}`);
      const responseData = await response.json();

      // Update localStorage exactly like index.js does

      if (response.status === 200) {
        toast.success('Referral balance added to your account!');
        // Refresh the page data
        setUserData(responseData?.usernameInfo);
        // Mirrors: window.location.replace("./dashbord.html")
        setTimeout(() => navigate('/dashboard'), 1500);
      } else {
        toast.error(responseData?.error || 'Failed to add balance');
      }
    } catch {
      toast.error('Network error. Please try again.');
    } finally {
      setAdding(false);
    }
  };

  // Mirrors handleCopyToClipeBord('ref') in index.js
  const handleCopyLink = async () => {
    if (!userData?.telegramName) return;
    // Mirrors: copyToClipeBordObj["refUrl"] = `./index.html?ref=${telegramName}`
    // then in handleCopyToClipeBord: `${urlLoc}${getUrl.replace('./', '')}`
    const refUrl = `${SITE_URL}/?ref=${userData.telegramName}`;
    try {
      await navigator.clipboard.writeText(refUrl);
      setCopied(true);
      toast.success('Referral link copied!');
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error('Failed to copy link');
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600" />
        </div>
      </DashboardLayout>
    );
  }

  const refBalance = Number(userData?.refralBalance ?? 0);
  const refList = userData?.refralList ?? [];
  const referralLink = `${SITE_URL}/?ref=${userData?.telegramName}`;

  return (
    <DashboardLayout>
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2 text-slate-700 hover:text-slate-900 mb-4"
          >
            <ArrowLeft size={20} />
            <span className="font-medium">Back to Dashboard</span>
          </button>
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-100 rounded-full mb-4">
              <Share2 className="text-purple-600" size={32} />
            </div>
            <h1 className="text-3xl font-bold text-slate-900 mb-2">Referral Program</h1>
            <p className="text-slate-500">Invite friends and earn rewards</p>
          </div>
        </div>

        {/* Referral Balance card — mirrors .account-info.ref-send in refer.html */}
        <div className="bg-gradient-to-br from-purple-600 to-pink-600 rounded-2xl p-6 mb-6 text-white shadow-xl">
          <div className="flex items-start justify-between mb-4">
            <div>
              {/* mirrors: <p><b>Referal Balance</b></p> and <h4 id="setRefBalance"> */}
              <p className="text-purple-100 font-medium mb-1">Referral Balance</p>
              <h2 className="text-4xl font-bold">${refBalance.toFixed(2)}</h2>
            </div>
            <div className="w-14 h-14 bg-white/20 rounded-full flex items-center justify-center">
              <Gift size={28} />
            </div>
          </div>

          {/* Referral link display — mirrors .username-handel.ref-handel */}
          <div className="bg-white/15 rounded-xl p-3 mb-4 flex items-center gap-3">
            <button
              onClick={handleCopyLink}
              className="flex items-center gap-2 px-3 py-1.5 bg-white/20 hover:bg-white/30 rounded-lg transition text-sm font-medium flex-shrink-0"
              title="Copy link"
            >
              {copied ? <CheckCircle size={16} /> : <Copy size={16} />}
              {copied ? 'Copied!' : 'Copy'}
            </button>
            {/* mirrors: <a class="wallet-addres" id="referal-link"> */}
            <span className="text-sm font-mono truncate text-purple-100">
              {referralLink}
            </span>
          </div>

          {/* Add Balance button — mirrors onclick="handelClickAddRefBalace(event)" */}
          <button
            onClick={handleAddRefBalance}
            disabled={adding || refBalance <= 0}
            className="w-full py-3 bg-white text-purple-700 font-bold rounded-xl hover:bg-purple-50 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {adding ? (
              <><Loader size={18} className="animate-spin" /> Adding to Balance...</>
            ) : (
              <><PlusCircle size={18} /> Add Balance to Account</>
            )}
          </button>
          {refBalance <= 0 && (
            <p className="text-center text-purple-200 text-xs mt-2">Invite friends to earn referral rewards</p>
          )}
        </div>

        {/* Referred users list — mirrors <ul id="displayRef"> in refer.html */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold text-slate-900 flex items-center gap-2">
              <Users size={18} className="text-purple-500" />
              Referral List
            </h3>
            <span className="bg-purple-100 text-purple-700 text-xs font-bold px-2.5 py-1 rounded-full">
              {refList.length} users
            </span>
          </div>

          {refList.length === 0 ? (
            <div className="text-center py-10 text-slate-400">
              <Users size={40} className="mx-auto mb-3 opacity-30" />
              <p className="font-medium">No referrals yet</p>
              <p className="text-sm mt-1">Share your referral link to get started</p>
            </div>
          ) : (
            // mirrors: jsonGetUserInfo?.refralList?.map(val => <li><a href=...>@username</a></li>)
            <ul className="divide-y divide-slate-100">
              {refList.map((ref, idx) => (
                <li key={ref._id || idx} className="flex items-center gap-4 py-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-purple-400 to-pink-400 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                    {ref.username?.[0]?.toUpperCase() || '?'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-slate-800 truncate">@{ref.username}</p>
                    {ref.date && (
                      <p className="text-xs text-slate-400">
                        {new Date(ref.date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
                      </p>
                    )}
                  </div>
                  {/* mirrors: <a href="./confarm-send.html?username=${val?.username}"> */}
                  <a
                    href={`/send?username=${ref.username}`}
                    className="text-xs text-purple-600 hover:text-purple-800 font-medium border border-purple-200 px-2 py-1 rounded-lg hover:bg-purple-50 transition"
                  >
                    Send →
                  </a>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* How it works */}
        <div className="bg-slate-50 border border-slate-200 rounded-2xl p-5">
          <h4 className="font-bold text-slate-800 mb-3">How it works</h4>
          <div className="space-y-2.5">
            {[
              'Copy your unique referral link above',
              'Share it with friends and contacts',
              'When they sign up using your link, they appear in your list',
              'Earn referral rewards credited to your referral balance',
              'Click "Add Balance" to transfer your referral earnings to your main account'
            ].map((step, i) => (
              <div key={i} className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-purple-100 text-purple-700 text-xs font-bold flex items-center justify-center mt-0.5">
                  {i + 1}
                </span>
                <p className="text-sm text-slate-600">{step}</p>
              </div>
            ))}
          </div>
        </div>

      </div>
    </DashboardLayout>
  );
};

export default Referral;
